<div class="ui-select">
  <!-- Cabecera: label + badge -->
  @if (showLabel()) {
    <div class="ui-select__header">
      <label class="ui-select__label" [attr.for]="triggerId()">{{ label() }}</label>
      @if (showRequirement()) {
        <span
          class="ui-select__requirement"
          [class.ui-select__requirement--error]="invalid() && required()"
          aria-hidden="true"
        >
          {{ requirementText() }}
        </span>
      }
    </div>
  }

  <!-- Trigger (campo visible) -->
  <button
    #triggerBtn
    type="button"
    class="ui-select__trigger"
    [id]="triggerId()"
    [class.ui-select__trigger--open]="isOpen()"
    [class.ui-select__trigger--invalid]="invalid()"
    [class.ui-select__trigger--disabled]="disabled()"
    [class.ui-select__trigger--placeholder]="!selected()"
    [class.ui-select__trigger--compact]="!showLabel()"
    [disabled]="disabled()"
    [attr.aria-haspopup]="'listbox'"
    [attr.aria-expanded]="isOpen()"
    [attr.aria-controls]="listboxId()"
    [attr.aria-required]="required() ? 'true' : null"
    [attr.aria-invalid]="invalid() ? 'true' : null"
    [attr.aria-describedby]="ariaDescribedBy()"
    [attr.aria-activedescendant]="!showSearch() && isOpen() ? activeOptionId() : null"
    (click)="toggle()"
    (keydown)="onTriggerKeydown($event)"
  >
    <span
      class="ui-select__trigger-text"
      [class.ui-select__trigger-text--tag]="!!selected()?.color"
    >
      @if (selected()?.color; as color) {
        <ui-tag [label]="selected()!.label" [color]="color" appearance="outline" />
      } @else {
        {{ selected()?.label ?? placeholder() }}
      }
    </span>

    <span class="ui-select__trigger-actions">
      <!-- Botón limpiar -->
      @if (selected() && !disabled() && allowClear()) {
        <span
          class="ui-select__clear"
          role="button"
          aria-label="Limpiar selección"
          tabindex="0"
          (click)="clear($event)"
          (keydown.enter)="clear($event)"
          (keydown.space)="clear($event)"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </span>
      }

      <!-- Chevron -->
      <span
        class="ui-select__chevron"
        [class.ui-select__chevron--open]="isOpen()"
        aria-hidden="true"
      >
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
          <path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
    </span>
  </button>

  <!-- Panel dropdown: position:fixed para escapar cualquier overflow:hidden padre -->
  @if (isOpen() && panelRect()) {
    <div
      class="ui-select__panel"
      [id]="listboxId()"
      role="listbox"
      [attr.aria-label]="label()"
      [style.top.px]="panelRect()!.top"
      [style.bottom.px]="panelRect()!.bottom"
      [style.left.px]="panelRect()!.left"
      [style.min-width.px]="panelRect()!.width"
      [style.width.px]="fitPanelToContent() ? null : panelRect()!.width"
    >
      <!-- Input de búsqueda -->
      @if (showSearch()) {
        <div class="ui-select__search-wrap">
          <span class="ui-select__search-icon" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" stroke-width="1.5"/>
              <path d="M10.5 10.5L14 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </span>
          <input
            #searchInput
            class="ui-select__search-input"
            type="text"
            role="combobox"
            [attr.aria-expanded]="isOpen()"
            [attr.aria-controls]="listboxId()"
            [attr.aria-activedescendant]="activeOptionId()"
            [placeholder]="searchPlaceholder()"
            [ngModel]="searchQuery()"
            (ngModelChange)="onSearch($event)"
            (click)="$event.stopPropagation()"
            (keydown)="onSearchKeydown($event)"
            autocomplete="off"
          />
        </div>
      }

      <!-- Lista de opciones -->
      <ul class="ui-select__list" role="presentation">
        @for (option of filteredOptions(); track option.id; let i = $index) {
          <li
            [id]="optionId(i)"
            class="ui-select__option"
            role="option"
            [class.ui-select__option--selected]="isSelected(option)"
            [class.ui-select__option--active]="activeIndex() === i"
            [class.ui-select__option--disabled]="option.disabled ?? false"
            [attr.aria-selected]="isSelected(option)"
            [attr.aria-disabled]="option.disabled ? 'true' : null"
            (click)="select(option)"
          >
            @if (option.color; as color) {
              <ui-tag [label]="option.label" [color]="color" appearance="outline" />
            } @else {
              <span class="ui-select__option-label">{{ option.label }}</span>
            }
            @if (isSelected(option)) {
              <span class="ui-select__option-check" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M2 7l4 4 6-6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            }
          </li>
        } @empty {
          <li class="ui-select__empty" role="option" aria-disabled="true">
            Sin resultados
          </li>
        }
      </ul>
    </div>
  }

  <!-- Mensaje de error -->
  @if (message() && invalid() && showMessage()) {
    <p
      class="ui-select__message ui-select__message--error"
      [id]="messageId()"
      role="alert"
    >
      {{ message() }}
    </p>
  }
</div>
